import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Supplier, isPdfNotesEmpty } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { openDillToast } from '../../../../../../utils/helpers';
import { DillButton } from '../../../../../general/Dill';
import { PdfNotesEditor } from '../PdfNotesEditor';

interface InvoicesSettingsSectionProps {
  supplier?: Supplier | null;
}

export const InvoicesSettingsSection = ({ supplier }: InvoicesSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [savedNotes, setSavedNotes] = useState(supplier?.invoicingSettings?.pdfNotes ?? '');
  const [pdfNotes, setPdfNotes] = useState(savedNotes);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const notes = supplier?.invoicingSettings?.pdfNotes ?? '';
    setSavedNotes(notes);
    setPdfNotes(notes);
  }, [supplier?.id]);

  // The editor re-serializes the saved note on load, so compare by meaning, not exact markup.
  const notesChanged =
    pdfNotes !== savedNotes && !(isPdfNotesEmpty(pdfNotes) && isPdfNotesEmpty(savedNotes));

  const handleSave = async () => {
    const notesToSave = isPdfNotesEmpty(pdfNotes) ? '' : pdfNotes;
    setSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id || '',
        supplierDetails: {
          // Spread existing settings so we don't drop other persisted fields.
          invoicingSettings: { ...supplier?.invoicingSettings, pdfNotes: notesToSave },
        },
      })
    );
    setSaving(false);

    if (result.type === 'supplier/update/fulfilled') {
      setSavedNotes(notesToSave);
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Invoice settings saved' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save invoice settings' });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Invoices</h4>
        <p className="text-xs text-gray-400 mt-0.5">Configure what prints on invoice PDFs.</p>
      </div>

      <div data-tooltip-id={!canEdit ? 'invoices-no-permission-tooltip' : undefined}>
        <PdfNotesEditor
          title="Invoice PDF notes"
          description="Printed in the notes box on every invoice PDF, below the payment notice."
          savedNotes={savedNotes}
          onChange={setPdfNotes}
          disabled={!canEdit}
        />
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'invoices-no-permission-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !notesChanged}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="invoices-no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
